import { z } from "zod";
import { WIDGET_THEME_NAMES } from "./themeNames.js";
export const VALID_FIT_VALUES = ["contain", "cover", "fill", "none"];
/**
 * validateFit: a string in the allowed set, else the supplied default.
 * Mirrors PlatformApiService.validateFit verbatim.
 */
export function mediaFit(defaultValue) {
    return z
        .unknown()
        .transform((v) => typeof v === "string" && VALID_FIT_VALUES.includes(v) ? v : defaultValue)
        .pipe(z.enum(VALID_FIT_VALUES));
}
/**
 * validateResponseStyle: bubble|plain, else bubble.
 * Mirrors PlatformApiService.validateResponseStyle verbatim.
 */
export const ResponseStyleSchema = z
    .unknown()
    .transform((v) => (v === "bubble" || v === "plain" ? v : "bubble"))
    .pipe(z.enum(["bubble", "plain"]));
/**
 * validateOpacity: number clamped 0..100, else 100.
 * Mirrors PlatformApiService.validateOpacity verbatim.
 */
export const OpacitySchema = z
    .unknown()
    .transform((v) => (typeof v === "number" && !Number.isNaN(v) ? Math.max(0, Math.min(100, v)) : 100));
const HEX_PATTERN = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
const FUNC_PATTERN = /^(rgb|rgba|hsl|hsla)\(\s*[\d.,\s%]+\)$/;
const NAMED_PATTERN = /^[a-zA-Z]+$/;
/**
 * validateColor: hex|rgb/hsl|named → passes; anything else (including XSS) → default.
 * Used ONLY for bubbleColor — the other five colors use colorPassthrough.
 * Mirrors PlatformApiService.validateColor verbatim.
 */
export function colorValidated(defaultValue) {
    return z
        .unknown()
        .transform((v) => typeof v === "string" && (HEX_PATTERN.test(v) || FUNC_PATTERN.test(v) || NAMED_PATTERN.test(v)) ? v : defaultValue);
}
/**
 * Verbatim legacy `|| default`: any truthy value passes through; falsy → default.
 * Used for the five non-bubbleColor color fields.
 */
export function colorPassthrough(defaultValue) {
    return z.unknown().transform((v) => (v ? v : defaultValue));
}
/**
 * MediaAdjustSchema: opacity (clamped) + fit (validated), both with supplied defaults.
 * Mirrors the background/avatar/sendButton/logo blocks in PlatformApiService.
 */
export function MediaAdjustSchema(fitDefault) {
    return z
        .object({
        opacity: OpacitySchema,
        fit: mediaFit(fitDefault),
    })
        .default({});
}
/**
 * ColorsSchema: only bubbleColor uses validateColor (XSS guard); the other colors
 * use colorPassthrough (truthy passthrough — verbatim `|| default` behavior).
 * Defaults sourced from PlatformApiService.transformPlatformConfig.
 */
export const ColorsSchema = z
    .object({
    primary: colorPassthrough("#007bff"),
    // accent drives the USER message bubble. Optional — existing configs won't
    // have it, and the widget falls back to primary when it's absent, so there's
    // no visual change until a tenant picks an accent.
    accent: z.string().optional(),
    // accentText drives text inside the USER message bubble. Optional for legacy configs.
    accentText: z.string().optional(),
    background: colorPassthrough("#ffffff"),
    primaryText: colorPassthrough("#ffffff"),
    systemText: colorPassthrough("#000000"),
    cardText: colorPassthrough("#000000"),
    bubbleColor: colorValidated("#F0F0F0"),
})
    .default({});
/**
 * TypographySchema: font settings with verbatim defaults from the source transforms.
 */
export const TypographySchema = z
    .object({
    fontFamily: z.string().default("system-ui, -apple-system, sans-serif"),
    fontSize: z.number().default(14),
    lineHeight: z.union([z.number(), z.string()]).default(1.5),
})
    .default({});
/**
 * ProductTilesSchema: tile display settings with verbatim defaults.
 */
export const ProductTilesSchema = z
    .object({
    size: z.enum(["small", "medium", "large"]).catch("medium").default("medium"),
    style: z.enum(["cards", "list"]).catch("cards").default("cards"),
    carouselConfig: z
        .object({
        autoScroll: z.boolean().default(false),
        scrollSpeed: z.number().default(5),
        showIndicators: z.boolean().default(true),
        itemsPerView: z.number().default(2),
    })
        .default({}),
})
    .default({});
/**
 * LogoSchema: logo display settings.
 * fit uses "contain" (same as avatar/sendButton; background is "cover").
 */
export const LogoSchema = z
    .object({
    alignment: z.enum(["left", "center", "right"]).catch("center").default("center"),
    titleText: z.string().default(""),
    showLogo: z.boolean().default(true),
    titleBarSize: z.enum(["none", "small", "medium", "large"]).catch("medium").default("medium"),
    titleBarStyle: z.enum(["solid", "surface"]).catch("solid").default("solid"),
    opacity: OpacitySchema,
    fit: mediaFit("contain"),
})
    .default({});
/**
 * IconsSchema: icon size settings.
 */
export const IconsSchema = z
    .object({
    avatarSize: z.enum(["small", "medium", "large"]).catch("medium").default("medium"),
})
    .default({});
/**
 * TextSchema: UI text strings with verbatim defaults.
 */
export const TextSchema = z
    .object({
    loadingText: z.string().default("Thinking..."),
    inputPlaceholder: z.string().default("Ask anything..."),
})
    .default({});
/**
 * DisclaimerSchema: optional disclaimer display.
 */
export const DisclaimerSchema = z
    .object({
    enabled: z.boolean().default(false),
    text: z.string().default(""),
})
    .default({});
/**
 * StylesSchema: response style + widget theme selection.
 */
export const StylesSchema = z
    .object({
    responseStyle: ResponseStyleSchema,
    theme: z.enum(WIDGET_THEME_NAMES).catch("classic").default("classic"),
})
    .default({});
/**
 * UtmParamSchema: UTM parameter record. Fields pass through verbatim (no conversion).
 * Uses passthrough() for any additional vendor/merchant keys.
 */
export const UtmParamSchema = z
    .object({ id: z.number(), parameter: z.string(), value: z.string(), enabled: z.boolean() })
    .passthrough();
//# sourceMappingURL=configPrimitives.js.map